import React, { useRef } from 'react';
import Decimal from 'decimal.js';
import { FieldArray, useFormikContext } from 'formik';
import capitalize from 'lodash/capitalize';
import * as yup from 'yup';

import { MainCard, Text } from '~/components/core/';
import Button from '~/components/core/Atomic/Buttons/Button';
import IconButton from '~/components/core/Atomic/Buttons/IconButton';
import MenuItem from '~/components/core/Atomic/MenuItem';
import { ErrorHelperTextFormik } from '~/components/core/Formik/ErrorHelperTextFormik';
import SwitchFormik from '~/components/core/Formik/SwitchFormik';
import { PlusIcon, TrashIcon } from '~/components/icons';
import TextFieldFormik from '~/components/TextFieldFormik';
import type { CoveredEntityWithParticipationsResponseCoverageParticipationForFESchema as CoverageParticipation } from '~/server_shared/openapi/data-contracts';
import type { CapacityAgreement, PolicyForFe } from '~/server_shared/swagger/data-contracts';
import { DECIMAL_ZERO } from '~/Utils/decimalUtils';
import { getRegionCode } from '~/Utils/regionUtils';

const CAPACITY_SLOT_BY_REGION = {
  US: 'Capacity Slot',
  UK: 'Capacity Slot',
  AU: 'Section',
  EU: 'Capacity Slot',
  default: 'Capacity Slot',
} as const satisfies Record<string, string>;

const TEXTS = {
  title: 'Coverage Participations',
  addParticipation: 'Add Participation',
  required: 'Required',
  capacitySlotLabel: () => `${CAPACITY_SLOT_BY_REGION[getRegionCode() || 'default']}`,
  allocationPercentageLabel: 'Allocation %',
  isLeadLabel: 'Is Lead',
  uniqueCapacitySlotIds: () => `${capitalize(CAPACITY_SLOT_BY_REGION[getRegionCode() || 'default'])}s must be unique`,
  allocationPercentageOutOfBounds: 'Allocation percentage must be between 0 and 100',
  allocationPercentagePrecisionBounds: 'Precision supported for up to 2 decimal places',
  totalAllocationPercentageOutOfBounds: (total: number) =>
    `Total allocation percentages must add up to 100%. Current total is ${total}%`,
  removeParticipation: 'Remove Participation',
};

type CoverageParticipationFormRowType = Omit<
  CoverageParticipation,
  'external_id' | 'allocation_percentage' | 'carrier_name' | 'slot_name'
> & {
  allocation_percentage: number;
  /* Newly added participations' external_id will be set server-side */
  external_id: string | null;
  /**
   * Used to ensure each newly added participation has a unique key for react mapping, as the index is not reliable for dynamic lists.
   * If present, this property will be ignored server-side.
   * */
  uniqueKey?: number;
};

interface CoverageParticipationsFormValues {
  participations: CoverageParticipationFormRowType[];
}

interface CoverageParticipationsFormikSectionInnerProps {
  participations: CoverageParticipationFormRowType[];
  capacityAgreements: CapacityAgreement[];
  push: (participation: CoverageParticipationFormRowType) => void;
  remove: (index: number) => void;
}

const CoverageParticipationsFormikRow = ({
  participation,
  activeParticipationSlotIds,
  index,
  capacityAgreements,
  onRemove,
}: {
  participation: CoverageParticipationFormRowType;
  activeParticipationSlotIds: string[];
  index: number;
  capacityAgreements: CapacityAgreement[];
  onRemove: () => void;
}) => {
  const { setValues } = useFormikContext();
  const handleIsLeadChange = (event: React.ChangeEvent<HTMLInputElement>, value: boolean) => {
    setValues((values: CoverageParticipationsFormValues) => ({
      ...values,
      participations: values.participations.map(
        (participation: CoverageParticipationFormRowType, participationIndex: number) =>
          participationIndex === index
            ? { ...participation, is_lead: value }
            : value
              ? { ...participation, is_lead: false }
              : participation
      ),
    }));
  };
  return (
    <>
      <TextFieldFormik id={`participations.${index}.capacity_slot_id`} label={TEXTS.capacitySlotLabel()} select>
        {/* @ts-expect-error - TextFieldFormik not yet typed and its type inferrence is flawed */}
        {capacityAgreements.flatMap((agreement) =>
          agreement.capacity_slots.length > 0
            ? [
                <div key={agreement.external_id} className="mt-8 px-8 first:mt-0">
                  <Text
                    variant={Text.VARIANTS.XS}
                    weight={Text.WEIGHTS.SEMI_BOLD}
                    colorVariant={Text.COLOR_VARIANTS.PRIMARY}
                  >
                    {agreement.carrier_name}
                  </Text>
                </div>,
                ...agreement.capacity_slots.map((slot) => (
                  <MenuItem
                    key={slot.external_id}
                    value={slot.external_id}
                    disabled={
                      activeParticipationSlotIds.includes(slot.external_id) &&
                      participation.capacity_slot_id !== slot.external_id
                    }
                  >
                    {slot.slot_name}
                  </MenuItem>
                )),
              ]
            : []
        )}
      </TextFieldFormik>
      <TextFieldFormik
        id={`participations.${index}.allocation_percentage`}
        label={TEXTS.allocationPercentageLabel}
        inputProps={{ type: 'number', inputMode: 'decimal', min: 0, max: 100 }}
      />
      <SwitchFormik
        id={`participations.${index}.is_lead`}
        label={TEXTS.isLeadLabel}
        onChange={handleIsLeadChange}
        hideErrorGap
      />
      <IconButton onClick={onRemove} className="self-center">
        <TrashIcon className="mb-2 mr-4" iconColor="currentColor" size={14} />
        <span className="sr-only">{TEXTS.removeParticipation}</span>
      </IconButton>
    </>
  );
};

const CoverageParticipationsFormikSectionInner = ({
  participations,
  capacityAgreements,
  push,
  remove,
}: CoverageParticipationsFormikSectionInnerProps) => {
  const uniqueKeyRef = useRef(1);
  const { submitCount } = useFormikContext<CoverageParticipationsFormValues>();
  const activeParticipationSlotIds = participations.map((participation) => participation.capacity_slot_id);
  return (
    <MainCard type="contained" className="mt-12 p-12">
      <Text variant={Text.VARIANTS.XS} weight={Text.WEIGHTS.MEDIUM}>
        {TEXTS.title}
      </Text>
      <div className="grid grid-cols-[1fr_1fr_auto_auto] gap-12">
        {(participations || []).map((participation, index) => (
          <CoverageParticipationsFormikRow
            key={participation.external_id || participation.uniqueKey}
            participation={participation}
            activeParticipationSlotIds={activeParticipationSlotIds}
            index={index}
            capacityAgreements={capacityAgreements}
            onRemove={() => remove(index)}
          />
        ))}
        <Button
          variant="text"
          color="primary"
          className="col-span-full justify-self-start"
          onClick={() => {
            push({
              external_id: null,
              capacity_slot_id: '',
              allocation_percentage: 0,
              is_lead: false,
              uniqueKey: uniqueKeyRef.current,
            });
            uniqueKeyRef.current += 1;
          }}
        >
          <PlusIcon className="mb-2 mr-4" iconColor="currentColor" size={14} />
          {TEXTS.addParticipation}
        </Button>
      </div>
      {submitCount > 0 ? <ErrorHelperTextFormik id="participations" withoutChildren /> : null}
    </MainCard>
  );
};

const CoverageParticipationsFormikSection = ({ policy }: { policy: PolicyForFe }) => {
  const { values } = useFormikContext<CoverageParticipationsFormValues>();
  const participations = values.participations;
  const capacityAgreements = policy?.capacity_agreements || [];
  return (
    <FieldArray name="participations">
      {({ push, remove }) => (
        <CoverageParticipationsFormikSectionInner
          participations={participations}
          push={push}
          remove={remove}
          capacityAgreements={capacityAgreements}
        />
      )}
    </FieldArray>
  );
};

export const getParticipationsInitialValues = (existingParticipations: CoverageParticipation[]) => {
  return (
    existingParticipations?.map((participation) => ({
      external_id: participation.external_id,
      capacity_slot_id: participation.capacity_slot_id,
      allocation_percentage: parseFloat(participation.allocation_percentage) || 0,
      is_lead: participation.is_lead,
    })) || []
  );
};

export const participationsValidationSchema = yup
  .array()
  .of(
    yup.object().shape({
      external_id: yup.string().nullable(),
      capacity_slot_id: yup.string().nullable().required(TEXTS.required),
      allocation_percentage: yup
        .number()
        .nullable()
        .required(TEXTS.required)
        .min(0, TEXTS.allocationPercentageOutOfBounds)
        .max(100, TEXTS.allocationPercentageOutOfBounds)
        .test('up-to-two-decimal-places', TEXTS.allocationPercentagePrecisionBounds, (value) => {
          if (!value) return true;
          return (value.toString().split('.')[1]?.length || 0) <= 2;
        }),
      is_lead: yup.boolean().nullable(),
    })
  )
  .test('unique-capacity-slot-ids', TEXTS.uniqueCapacitySlotIds, (participations) => {
    if (!participations) return true;
    const capacitySlotIds = participations.map((participation) => participation.capacity_slot_id);
    return capacitySlotIds.length === new Set(capacitySlotIds).size;
  })
  .test('total-allocation-percentage', (participations, context) => {
    if (!participations || participations.length === 0) return true;
    const totalAllocationPercentage = participations.reduce(
      (acc, participation) => acc.plus(new Decimal(participation?.allocation_percentage || 0)),
      DECIMAL_ZERO
    );
    const isValid = totalAllocationPercentage.equals(new Decimal(100));
    if (!isValid)
      return context.createError({
        message: TEXTS.totalAllocationPercentageOutOfBounds(totalAllocationPercentage.toNumber()),
      });
    return isValid;
  });

export default CoverageParticipationsFormikSection;
